import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import LinearProgress from '@mui/material/LinearProgress';
import { Pagination } from '../../../../model/pagination';
import { Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { DefaultDialogProps } from '../../../../components/default-dialog';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import SettingsIcon from '@mui/icons-material/Settings';
import ContractAppService from '../../../../application/platform/scp/contract/contract-app-service';
import { IAmendmentPageRequest } from '../../../../model/scp/contract/amendment/request/amendment-paged-request';
import { IAmendmentPagedResponse } from '../../../../model/scp/contract/amendment/response/amendment-paged-response';
import moment from 'moment';
import { useBaseLayout } from '../../../../components/context/use-base-layout';
import { dataGridLocaleConfig } from '../../../../config/data-grid-locale';

export interface IAmendmentDataTableProps {
    parameters: IAmendmentPageRequest;
    contractId: number;
};

interface IAmendmentDataTableMenuContextProp {
    amendment: IAmendmentPagedResponse;
    contractId: number;
    loadData: (parameters: IAmendmentPageRequest) => void;
};

function MenuPopupState({ props }: { props: IAmendmentDataTableMenuContextProp }) {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const goToDetails = () => {
        handleClose();
        navigate(`/contratos/${props.contractId}/aditivos/${props.amendment.sequentialContractTerm}`);
    }

    return (
        <div>
            <IconButton
                color="primary"
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}>
                <SettingsIcon />
            </IconButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                <MenuItem onClick={async () => {
                    handleClose();
                    navigate(`/contratos/${props.contractId}/aditivos/publicar/${props.amendment.sequentialContractTerm}`);
                }}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
            </Menu>
        </div>
    );
}

export default function AmendmentDataTable({ props }: { props: IAmendmentDataTableProps }) {
    const [response, setResponse] = useState<Pagination<IAmendmentPagedResponse>>(new Pagination<IAmendmentPagedResponse>);
    const [amendmentDataTableProps, setAmendmentDataTableProps] = useState<IAmendmentDataTableProps>(props);
    const [loadMoreData, setLoadMoreData] = useState(true);

    const loadData = (parameters: IAmendmentPageRequest) => {
        (async () => {
            if (parameters !== undefined) {
                setLoadMoreData(true);
                const data = await ContractAppService.listContractAmendments(parameters);
                console.log(data);
                setResponse(data);
                setLoadMoreData(false);
            }
        })();
    }

    const columns: GridColDef<IAmendmentPagedResponse>[] = [
        {
            field: 'contractTermNumber',
            headerName: 'Nº Termo',
            flex: 1
        },
        {
            field: 'contractTermType',
            headerName: 'Tipo',
            flex: 1,
            renderCell: (params) => {
                return <Chip label={params.row.contractTermType || '-'} color="primary" />
            }
        },
                {
            field: 'supplierCompanyName',
            headerName: 'Fornecedor',
            flex: 1
        },
        {
            field: 'signDate',
            headerName: 'Data Assinatura',
            flex: 1,
            renderCell: (params) => {
                return params.row.signDate ? moment(params.row.signDate).format('DD/MM/YYYY') : '-';
            }
        },
        {
            field: 'increaseValue',
            headerName: 'Valor Acrescido',
            flex: 1,
            renderCell: (params) => {
                return params.row.increaseValue ?
                    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(params.row.increaseValue) : '-';
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 100,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                return (
                    <MenuPopupState props={{ amendment: params.row, contractId: props.contractId, loadData: loadData }}></MenuPopupState>
                );
            }
        }
    ];

    useEffect(() => {
        loadData(props.parameters);
        setAmendmentDataTableProps(props);
    }, [props.contractId]); // Apenas re-executa quando contractId mudar

    return (
        <React.Fragment>
            <DataGrid
                rows={response.items}
                columns={columns}
                page={(amendmentDataTableProps?.parameters.page || 1) - 1}
                pageSize={response.pageSize}
                rowCount={response.totalRecords}
                disableSelectionOnClick
                getRowId={(row) => row.sequentialContractTerm || 0}
                onPageChange={(newPage) => {
                    const newParameters = { ...amendmentDataTableProps.parameters, page: newPage + 1 };
                    setAmendmentDataTableProps({ ...amendmentDataTableProps, parameters: newParameters });
                    loadData(newParameters);
                }}
                paginationMode="server"
                components={{
                    LoadingOverlay: LinearProgress,
                }}
                loading={loadMoreData}
                rowsPerPageOptions={[response.pageSize]}
                experimentalFeatures={{ newEditingApi: true }}
                localeText={dataGridLocaleConfig}
            />
        </React.Fragment>
    )
}
